/**
 * `GET /user/me/permissions?organization_id=…` — the caller's effective action
 * set for one org. Ported from `bimgo-ai-web`'s `getUserPermissions`.
 *
 * Frontend gating (which settings tab shows, whether the edit control renders)
 * is a **UX accelerator only** — api-v2's `require_action` remains the
 * authoritative check on every protected route. The response is the resolved
 * model **directly**, not a `{ success, data }` envelope.
 *
 * The web calls api-v2 directly (Corrections #3): the renderer owns the Supabase
 * session, so it is the only thing holding a token.
 */
import { OrganizationsError, readDetail } from "./organizations";
import { bearer } from "./accessToken";
import { apiFetch } from "./apiFetch";

/** Reused so callers can `catch (e) { if (e instanceof ApiError) … }` uniformly. */
export { OrganizationsError as ApiError };

export interface UserPermissions {
  user_id: string;
  email: string;
  organization_id: string;
  roles: { system: string[]; org: string[] };
  actions: string[];
}

export async function fetchUserPermissions(
  token: string | null,
  orgId: string | null,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<UserPermissions> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");
  if (!orgId) throw new OrganizationsError({ kind: "unauthenticated" }, "no organization");

  const fetchImpl = deps.fetchImpl ?? apiFetch;
  const url = `${deps.apiBaseUrl.replace(/\/+$/, "")}/user/me/permissions?organization_id=${encodeURIComponent(orgId)}`;

  let response: Response;
  try {
    response = await fetchImpl(url, { headers: { Authorization: bearer(token) } });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }

  if (response.status === 401 || response.status === 403) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = readDetail(body);
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }
  return body as UserPermissions;
}
